<template>
  <v-container fluid class="pa-4 pb-16">
    <v-card class="mb-4 pb-2" color="blue-darken-4" elevation="2" variant="flat" :loading="loading">
      <template #loader>
        <v-progress-linear
            color="blue-darken-3"
            height="3.5"
            v-if="loading"
            indeterminate
        />
      </template>
      <v-card-title class="d-flex align-start mt-1 justify-space-between flex-wrap">
        <div>
          <div class="text-h5 font-weight-bold ">
            {{ targetOwner.name || "-" }} - {{ salesTarget.code || "-" }}
          </div>
          <div class="d-flex align-center mt-3">
            <v-chip :color="approvalStatusColor" size="small" variant="flat">
              {{ salesTarget.approval_status || statusLabel }}
            </v-chip>
            <v-chip v-if="salesTarget.level_type" color="yellow" size="small" class="ml-2" variant="tonal">
              {{ salesTarget.level_type }}
            </v-chip>
          </div>
        </div>
        <div style="display: flex; flex-direction: column; gap:4px; justify-items: flex-end">
          <v-btn
              v-if="approvalDetails"
              color="yellow"
              size="small"
              variant="flat"
              prepend-icon="mdi-clock-alert-outline"
              elevation="2"
              @click="$refs.approvalActionSheet.open()"
          >
            Approval History
          </v-btn>
          <v-btn
              size="small"
              color="secondary"
              variant="flat"
              prepend-icon="mdi-reload"
              elevation="2"
              :disabled="loading"
              @click="refreshTargetDetails"
          >
            Refresh
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <v-row dense class="mb-2">
      <v-col v-for="summary in summaryCards" :key="summary.title" cols="12" sm="6" md="3">
        <v-card>
          <v-card-text class="d-flex flex-column">
            <div class="d-flex mb-4">
              <v-icon :icon="summary.icon"></v-icon>
              <div class="text-subtitle-3 ml-1 font-weight-bold">{{ summary.title }}</div>
            </div>
            <div class="d-flex justify-center">
              <v-chip
                variant="tonal"
                color="primary"
              >
                {{ summary.value }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card :loading="loading" :disabled="loading">
      <template #loader>
        <v-progress-linear
            color="blue-darken-3"
            height="3.5"
            v-if="loading"
            indeterminate
        />
      </template>
      <v-tabs v-model="tab" color="primary" align-tabs="end">
        <v-tab
          v-for="tabItem in tabList"
          :key="tabItem.value"
          :value="tabItem.value"
          class="text-subtitle-2 font-weight-semibold"
        >
          <v-icon start>{{ tabItem.icon }}</v-icon>
          {{ tabItem.title }}
        </v-tab
            >
      </v-tabs>

      <v-divider />

      <v-window v-model="tab">
        <sales-target-overview
            :target-data="overviewTargetData"
            :progress-data="progressData" />
        <sales-target-monthly
            :monthly-target-data="monthlyTargetData"
        />
        <sales-target-breakdown
            :breakdown-data="breakdownData"
        />
<!--        <sales-target-approval-history-->
<!--            :approval-history-data="dashboard.approval_history || []"-->
<!--        />-->
      </v-window>
    </v-card>

    <approval-action-sheet
      ref="approvalActionSheet"
      :approval-details="approvalDetails"
      :approval-status="salesTarget.approval_status"
      subject="Sales Target"
      @updated="refreshTargetDetails"
    />
  </v-container>
</template>

<script>
import { toast } from "vue3-toastify";
import { formatDateDash } from "@/plugins/helpers";
import SalesTargetOverview from "@/components/module/sales/targetManagement/salesTargetOverview.vue";
import SalesTargetMonthly from "@/components/module/sales/targetManagement/salesTargetMonthly.vue";
import SalesTargetBreakdown from "@/components/module/sales/targetManagement/salesTargetBreakdown.vue";
import SalesTargetApprovalHistory from "@/components/module/sales/targetManagement/salesTargetApprovalHistory.vue";
import ApprovalActionSheet from "@/components/utils/approvalActionSheet.vue";
import formattedDate from "@/plugins/dateFormatter.js";

export default {
  components: {
    SalesTargetOverview,
    SalesTargetMonthly,
    SalesTargetBreakdown,
    SalesTargetApprovalHistory,
    ApprovalActionSheet,
  },

  data: () => ({
    tab: "overview",
    targetId: null,
    loading: false,
    salesTarget: {},
    tabList: [
      { title: "Overview", icon: "mdi-view-dashboard-outline", value: "overview" },
      { title: "Monthly Target", icon: "mdi-calendar-month-outline", value: "monthly-target" },
      { title: "Breakdown", icon: "mdi-chart-box-outline", value: "breakdown" },
      // { title: "Approval History", icon: "mdi-history", value: "approval-history" },
    ],
  }),

  computed: {
    dashboard() {
      return this.salesTarget.dashboard_tabs || {};
    },
    overview() {
      return this.dashboard.overview || {};
    },
    approvalDetails() {
      return this.salesTarget.approval_details || {};
    },
    targetOwner() {
      return this.overview.target_owner || this.salesTarget.employee || {};
    },
    statusLabel() {
      return this.salesTarget.status === 1 ? "Active" : "Inactive";
    },
    approvalStatusColor() {
      return {
        approved: "success",
        pending: "warning",
        cancelled: "error",
        rejected: "error",
      }[this.salesTarget.approval_status?.toLowerCase()] || "grey";
    },
    overviewTargetData() {
      return {
        ...this.salesTarget,
        employee: this.targetOwner,
        department: this.overview.department?.name || this.overview.department || "-",
        team: this.overview.team?.team_name || this.overview.team?.name || this.overview.team || "-",
        year: this.overview.target_year,
        targetType: this.salesTarget.level_type,
        period: this.overview.period
          ? `${this.overview.period.start_date}  -  ${this.overview.period.end_date}`
          : "-",
        remarks: this.overview.remarks,
      };
    },
    progressData() {
      const progress = this.overview.progress || {};
      const amountTarget = Number(progress.target_amount || 0);
      const quantityTarget = Number(progress.target_quantity || 0);
      return {
        amountAchievement: this.percent(progress.achieved_amount, amountTarget),
        quantityAchievement: this.percent(progress.achieved_quantity, quantityTarget),
        currentPosition: progress.current_position || "-",
        remainingMonths: progress.remaining_months ?? 0,
        requiredRunRate: this.$numberFormat(progress.required_run_rate),
      };
    },
    monthlyTargetData() {
      return (this.dashboard.monthly_targets || []).map((item) => ({
        ...item,
        targetAmount: this.$numberFormat(item.target_amount),
        targetQuantity: this.$numberFormat(item.target_quantity),
        achievement: item.status === "upcoming"
          ? "Upcoming"
          : this.$numberFormat(item.achieved_amount),
      }));
    },
    breakdownData() {
      return this.dashboard.breakdown || [];
    },
    summaryCards() {
      return [
        { title: "Yearly Target", value: this.$numberFormat(this.dashboard.yearly_target), icon: "mdi-target" },
        { title: "Yearly Quantity Target", value: this.$numberFormat(this.dashboard.yearly_quantity) , icon: "mdi-target-variant"},
        { title: "Achieved Amount", value: this.$numberFormat(this.dashboard.achieved_amount), icon: "mdi-bullseye-arrow" },
        { title: "Remaining Target", value: this.$numberFormat(this.dashboard.remaining_target), icon: "mdi-bullseye" },
      ];
    },
  },

  created() {
    this.targetId = this.$route.params.id;
    this.loadTarget();
  },

  methods: {
    formatDateDash,
    formattedDate,
    percent(value, total) {
      return total > 0 ? Math.min(100, Math.max(0, Number(value || 0) / total * 100)) : 0;
    },
    async loadTarget() {
      this.loading = true;
      try {
        this.salesTarget = await this.$store.dispatch("getSaleEmployeeTargetById", this.targetId);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load sales target details");
      } finally {
        this.loading = false;
      }
    },
    async refreshTargetDetails() {
      await this.loadTarget();
    },
  },
};
</script>
